import { identity } from 'lodash';
import { actions as pagerActions } from 'rp-react-pagination';
import { fetchData } from './gridDataTable';
import { queryUrl } from '../helpers/urlBuilder';
import {
    shortlistStateKey,
    getSearchFilterOptionsKey,
    shortlistPagerLength,
    getSearchQueryParams
} from '../helpers/shortlistUtils';
import tableConfig from '../config/shortlistTable';
import { actions as tableActions } from '../modules/DataTable';

export const SHORTLIST_REFINEMENT_CHANGE = 'SHORTLIST_REFINEMENT_CHANGE';
export const SHORTLIST_REFINEMENT_RESET = 'SHORTLIST_REFINEMENT_RESET';
export const SHORTLIST_SALE_NAME_REFINEMENT_RESET = 'SHORTLIST_SALE_NAME_REFINEMENT_RESET';

export const SHORTLIST_DATA_CHANGE = 'SHORTLIST_DATA_CHANGE';

export const SHORTLIST_FAILURE_LIGHTBOX_OPEN =
    'SHORTLIST_FAILURE_LIGHTBOX_OPEN';
export const SHORTLIST_FAILURE_LIGHTBOX_CLOSE =
    'SHORTLIST_FAILURE_LIGHTBOX_CLOSE';

const saleNameKey = 'saleName';
const horseNameKey = 'horseName';
const horseSexKey = 'horseSex';
const horseAgeKey = 'horseAge';
const sireNameKey = 'sireName';
const damNameKey = 'damName';
const sireOfDamNameKey = 'sireOfDamName';
const vendorKey = 'sellerName';
const buyerKey = 'buyerDetail';
const enteredKey = 'entered';
const priceKey = 'price';
const notesKey = 'withNotes';

function matchFilterKeyToSearchParam(filterKey) {
    const MATCHES = {
        [saleNameKey]: 'sale_name',
        [horseNameKey]: 'horse_name',
        [horseSexKey]: 'horse_sex',
        [horseAgeKey]: 'horse_age',
        [sireNameKey]: 'sire_name',
        [damNameKey]: 'dam_name',
        [sireOfDamNameKey]: 'sire_of_dam_name',
        [vendorKey]: 'seller_name',
        [buyerKey]: 'buyer_detail',
        [enteredKey]: 'entered',
        [priceKey]: 'price',
        [notesKey]: 'with_notes'
    };

    return MATCHES[filterKey] ? MATCHES[filterKey] : filterKey;
}

export function openShortlistFailureLightbox() {
    return {
        type: SHORTLIST_FAILURE_LIGHTBOX_OPEN,
    };
}

export function closeShortlistFailureLightbox() {
    return {
        type: SHORTLIST_FAILURE_LIGHTBOX_CLOSE,
    };
}

function updateSearchData(payload) {
    return {
        type: SHORTLIST_DATA_CHANGE,
        payload,
    };
}

function changeShortlistRefinement(payload) {
    return {
        type: SHORTLIST_REFINEMENT_CHANGE,
        payload,
    };
}

function resetShortlistRefinement() {
    return {
        type: SHORTLIST_REFINEMENT_RESET,
    };
}

function resetSearchResultsSaleNameRefinement() {
    return {
        type: SHORTLIST_SALE_NAME_REFINEMENT_RESET,
    };
}

function shouldUpdateTable(state) {
    return !state[shortlistStateKey].dataTable.initialized;
}

/**
 * Update number of found lots by search
 *
 * @param {number} lotsFound
 * @returns {{}} Action
 */
export function updateSearchLotsFound(lotsFound) {
    return updateSearchData({ lotsFound });
}

export function updateShortlistTable(page) {
    return (dispatch, getState) => {
        const url = getState()[shortlistStateKey].gridDataTable.url || tableConfig.url;

        return dispatch(
            fetchData(
                shortlistStateKey,
                queryUrl(url,
                    getSearchQueryParams(
                        getState()[shortlistStateKey].refinement,
                        page
                    )
                )
            ))
            .then(({ pagination }) => Promise.all([
                dispatch(pagerActions.pagerUpdate(
                    shortlistStateKey,
                    pagination,
                    shortlistPagerLength
                )),
                dispatch(updateSearchLotsFound(pagination.totalItems))
            ]));
    };
}

export function updateShortlistTableIfNeeded(page) {
    return (dispatch, getState) => (
        shouldUpdateTable(getState()) ?
            updateShortlistTable(page)(dispatch, getState) :
            dispatch(tableActions.importData(shortlistStateKey))
    );
}

function applyShortlistRefinement(payload) {
    return (dispatch) => {
        dispatch(changeShortlistRefinement(payload));
    };
}

export function applyDefaultShortlistRefinement() {
    return (dispatch) => {
        dispatch(resetShortlistRefinement());
    };
}

export function applyDefaultSearchResultsSaleNameRefinement() {
    return (dispatch) => {
        dispatch(resetSearchResultsSaleNameRefinement());
    };
}

function fetchFilterOptions(filterKey) {
    return () => {
        const urlPrefix = '/bloodstock/sales/my-shortlist/';
        return fetch(
            `${urlPrefix}refine/${matchFilterKeyToSearchParam(filterKey)}`,
            { credentials: 'include' }
        ).then(response => response.json());
    };
}

function createShortlistApplyFilterAction(filterKey, valueGetter = identity) {
    return rawValue => applyShortlistRefinement({
        [filterKey]: valueGetter(rawValue)
    });
}

function createShortlistLoadOptionsAction(filterKey) {
    return () => dispatch => dispatch(fetchFilterOptions(filterKey))
        .then(options => dispatch(updateSearchData({
            [getSearchFilterOptionsKey(filterKey)]: options
        })));
}

export const applyShortlistSaleNameFilter = createShortlistApplyFilterAction(saleNameKey);
export const loadSaleNameOptions = createShortlistLoadOptionsAction(saleNameKey);

export const applyShortlistHorseNameFilter = createShortlistApplyFilterAction(horseNameKey);
export const loadHorseNameOptions = createShortlistLoadOptionsAction(horseNameKey);

export const applyShortlistHorseSexFilter = createShortlistApplyFilterAction(horseSexKey);
export const loadHorseSexOptions = createShortlistLoadOptionsAction(horseSexKey);

export const applyShortlistHorseAgeFilter = createShortlistApplyFilterAction(horseAgeKey);
export const loadHorseAgeOptions = createShortlistLoadOptionsAction(horseAgeKey);

export const applyShortlistSireNameFilter = createShortlistApplyFilterAction(sireNameKey);
export const loadSireNameOptions = createShortlistLoadOptionsAction(sireNameKey);

export const applyShortlistDamNameFilter = createShortlistApplyFilterAction(damNameKey);
export const loadDamNameOptions = createShortlistLoadOptionsAction(damNameKey);

export const applyShortlistSireOfDamNameFilter = createShortlistApplyFilterAction(
    sireOfDamNameKey
);

export const loadSireOfDamNameOptions = createShortlistLoadOptionsAction(sireOfDamNameKey);

export const applyShortlistVendorFilter = createShortlistApplyFilterAction(vendorKey);
export const loadVendorOptions = createShortlistLoadOptionsAction(vendorKey);

export const applyShortlistBuyerFilter = createShortlistApplyFilterAction(buyerKey);
export const loadBuyerOptions = createShortlistLoadOptionsAction(buyerKey);

export const applyShortlistEnteredFilter = createShortlistApplyFilterAction(enteredKey);

export const applyShortlistSoldFilter = createShortlistApplyFilterAction(priceKey);

export const applyShortlistNotesFilter = createShortlistApplyFilterAction(notesKey);



// WEBPACK FOOTER //
// ./src/js/actions/shortlist.js